"use client";

import { GridSortModel } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";

import { EmptyView } from "@/components/empty-view";
import { ErrorView } from "@/components/error-view";
import { getBuyingInfoCols } from "@/features/analysis/chip/us/insider/data/buying-info-cols";
import { getBuyingInfoRows } from "@/features/analysis/chip/us/insider/data/buying-info-rows";
import { useInsiderBuyingInfo } from "@/features/analysis/chip/us/insider/services/insider-buying-info";
import { UpdatedText } from "@/features/analysis/components/UpdatedText";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

import { Skeletons } from "ui/widget/skeletons";
import { XDataGrid } from "ui/widget/x-data-grid";

type Props = {
  lang: Lang;
  symbol: string;
};

export const DetailsTable = ({ lang, symbol }: Props) => {
  const { t } = useLang();

  const { profile, isUnauthenticated } = useAuth();
  const isBlocked = isUnauthenticated || profile?.plan === "Free";

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);

  const { data, isLoading, isError, isFetching } = useInsiderBuyingInfo({
    symbol,
    page,
    pageSize,
    sortBy,
  });
  const cols = useMemo(
    () => getBuyingInfoCols({ t, isBlocked }),
    [t, isBlocked]
  );
  const rows = useMemo(() => getBuyingInfoRows({ data }), [data]);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length === 0) return setSortBy("");
    const sortKey = `${model[0].field}${model[0].sort === "asc" ? 1 : 0}`;
    setSortBy(sortKey);
  }, []);

  return (
    <>
      {isLoading ? (
        <LoadingView />
      ) : isError ? (
        <ErrorView className="h-[300px]">{t.error.serverError}</ErrorView>
      ) : (
        <div>
          <XDataGrid
            sx={{
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                display: "none",
              },
            }}
            columns={cols}
            rows={rows}
            paginationMode="server"
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            loading={isFetching}
            disableColumnMenu
            rowCount={data?.total ?? 0}
            paginationModel={{ page: page - 1, pageSize }}
            onPaginationModelChange={({ page, pageSize }) => {
              setPage(page + 1);
              setPageSize(pageSize);
            }}
            emptyView={() => <EmptyView>{t.error.noData}</EmptyView>}
            rowHeight={46}
            lang={lang}
          />
        </div>
      )}
      <UpdatedText
        lang={lang}
        updatedAt={data?.updatedAt ?? ""}
        frequency="d"
        className="ml-auto"
      />
    </>
  );
};

const LoadingView = () => (
  <Skeletons
    columns={3}
    rows={10}
    maxHeight={220}
    className="px-4 py-8"
    colClassName="gap-5"
  />
);
